import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { isTournamentPool, isTournamentPage } from './Common';
import { singleMatchSortFunc, updateCondition } from '../../BetSlip/AddSlipBtn';
import { getPersonalSettings } from '../../Home/PersonalSettings/config';
import { useBetSlip } from '../../BetSlip/BetSlipHooks';
import { Cookies } from '../../Common/CookieHelper';
import { Query } from '../../Common/graphqlFetch';
import { getMatchQuery, getQueryVariables, getTournamentQuery } from '../Data/DataQuery';
// Private method to get betshare API response by id
const getBetShare = async (betshareId, errorCb) => {
    let url = `${window.globalConfig.BETSHARE_URL}${betshareId}`;
    return await fetch(url)
        .then((res) => res.json())
        .catch((error) => {
            console.error(error);
            errorCb?.();
        });
};

export const useBetShareHooks = (contentRef) => {
    const AllPoolsMinBet = getPersonalSettings('football');
    const { addToBetSlip } = useBetSlip();
    const content = contentRef.current
    const matchList = content?.matchData?.matches;
    // Variables to be used to form the bet line object
    let betLineList = [];
    let checkedLinesList = [];
    let checkedLinesObj = {};

    // State Management
    const [isBetshareExpired, setIsBetshareExpired] = useState(false);

    const [betShareResponse, setBetShareResponse] = useState(null);
    const [fetchBetShareLoading, setFetchBetShareLoading] = useState(true);
    const [fetchBetShareError, setFetchBetShareError] = useState(false);
    const [betShareData, setBetShareData] = useState({
        betType: null,
        betSlipPool: null,
        lineId: null,
        combId: null,
        poolId: null,
        matchId: null,
        tournamentId: null
    });

    const [matchData, setMatchData] = useState(null);
    const [fetchMatchLoading, setFetchMatchLoading] = useState(true);
    const [fetchMatchError, setFetchMatchError] = useState(false);

    const [tournamentData, setTournamentData] = useState(null);
    const [fetchTournamentLoading, setFetchTournamentLoading] = useState(true);
    const [isComplete, setIsComplete] = useState(false);
    const [fetchTournamentError, setFetchTournamentError] = useState(false);

    // Wrapper functions to fetch matches and tournaments
    const fetchMatchData = async (params) => {
        return await Query({
            query: getMatchQuery(),
            variables: getQueryVariables(params)
        });
    };

    const fetchTournamentData = async (params) => {
        return await Query({
            query: getTournamentQuery(),
            variables: getQueryVariables(params)
        });
    };

    const handleAddBetslip = async (betLineList) => {
        if (sessionStorage.getItem('eWinParaDone') == '1') {
            addToBetSlip(betLineList);            
        }else{
            setTimeout(() => {
                handleAddBetslip(betLineList)
            }, 1000);
        }
    }

    // Fetch BetShare Data by using the betshareId from cookies
    useEffect(() => {
        const getBetShareData = async () => {
            try {
                const betShareId = Cookies.getData('betshareId');

                if (betShareId) {
                    const response = await getBetShare(betShareId, () => setFetchBetShareError(true));
                    // await QueryConfig('channelParas', window.globalConfig.CHANNEL_PARA_URL);
                    // Set betShareResponse to "response" ONLY if Params exists and is non-empty after parsing
                    setBetShareResponse(response?.Params && !isEmpty(JSON.parse(response?.Params)) ? response : null);
                }
            } catch (err) {
                console.error(err);
                setFetchBetShareError(true);
            } finally {
                setFetchBetShareLoading(false);
            }
        };

        getBetShareData();
    }, []);

    // Parse the betshare response and set the betShareData state
    useEffect(() => {
        if (fetchBetShareLoading || !betShareResponse) return;

        setIsBetshareExpired(betShareResponse.IsExpired);
        
        if (!betShareResponse.IsExpired) {
            const betParams = JSON.parse(betShareResponse.Params);
            const betType = betParams.parameters.poolType;
            const betSlipPool = betParams.parameters.pools[0];

            setBetShareData({
                betType,
                betSlipPool,
                lineId: betSlipPool.lines[0].LineId,
                combId: betSlipPool.lines[0].sels,
                poolId: betSlipPool.PoolId,
                matchId: isTournamentPool(betType) ? null : betSlipPool.MatchID,
                tournamentId: isTournamentPool(betType) ? betSlipPool.tournamentID : null,
                bettingSource: betParams.sourceFrom
            });
        }
    }, [betShareResponse, fetchBetShareLoading]);

    // Fetch Match and Tournament Data
    useEffect(() => {
        const getMatchData = async () => {
            try {
                const matchData = await fetchMatchData({ page: 'ALLODDS', matchIds: [betShareData.matchId] });

                if (!matchData?.data?.matches || matchData.data.matches.length === 0) {
                    console.error(`Match not found, match id ${betShareData.matchId}`);
                    setFetchMatchError(true);
                }
                setMatchData(matchData.data.matches[0]);
            } catch (err) {
                console.error(err);
                setFetchMatchError(true);
            } finally {
                setFetchMatchLoading(false);
            }
        };

        const getTournamentData = async () => {
            try {
                const tournamentData = await fetchTournamentData({
                    page: 'TOURNAMENT',
                    tournIds: [betShareData.tournamentId]
                });

                if (!tournamentData?.data?.tournaments || tournamentData.data.tournaments.length === 0) {
                    console.error(`Tournament not found, tournament id ${betShareData.tournamentId}`);
                    setFetchTournamentError(true);
                }
                    setTournamentData(tournamentData.data.tournaments[0]);
            } catch (err) {
                console.error(err);
                setFetchTournamentError(true);
            } finally {
                setFetchTournamentLoading(false);
            }
        };

        if (betShareData?.matchId && !isBetshareExpired) {
            getMatchData();
        }

        if (betShareData?.tournamentId && !isBetshareExpired) {
            getTournamentData();
        }
    }, [isBetshareExpired, betShareData?.matchId, betShareData?.tournamentId]);

    // Following logic mainly comes from handleClickAddSlipBtn function in AddSlipBtn component
    useEffect(() => {
        if (fetchMatchLoading && fetchTournamentLoading) return; // Only running this effect if any of these has finished loading

        if (fetchBetShareError || fetchMatchError || fetchTournamentError) return; // Prevent running this effect if there was an error when fetching data

        if (isBetshareExpired) return; // Early return if betshare is expired
        if(isComplete) return;

        if(isTournamentPool(betShareData.betType) && !matchList && !isTournamentPage(content?.betType)) return;
        
        if (isTournamentPool(betShareData.betType)) {    
            const currentPool = tournamentData.foPools?.find((pool) => pool.id === betShareData.poolId);
            const currentLine = currentPool?.lines?.find((line) => line.lineId === betShareData.lineId);
            const currentCombination = currentLine?.combinations?.find((comb) => comb.combId === betShareData.combId);

            // Early return if pool/line/combination is not found
            if (![currentPool, currentLine, currentCombination].every(Boolean)) {
                console.error('Missing pool/line/combination data');
                return;
            }

            let currentCombNameObj = currentCombination.selections[0];
            const comb = currentCombination.str;
            
            if (betShareData.betType === 'GPF') {
                const combFirstTeam = comb[1];
                const combSecondTeam = comb[4];
                const combId1 = currentCombination.selections.find((team) => team.str === combFirstTeam);
                const combId2 = currentCombination.selections.find((team) => team.str === combSecondTeam);
                const name_en = `${combId1.str} ${combId1.name_en} - ${combId2.str} ${combId2.name_en}`;
                const name_ch = `${combId1.str} ${combId1.name_ch} - ${combId2.str} ${combId2.name_ch}`;
                currentCombNameObj = {
                    name_en,
                    name_ch
                };
            } else if (betShareData.betType === 'GPW') {
                const combSelection = currentCombination.selections[0];
                const name_en = `${combSelection.str} ${combSelection.name_en}`;
                const name_ch = `${combSelection.str} ${combSelection.name_ch}`;
                currentCombNameObj = {
                    name_en,
                    name_ch
                };
            }

            checkedLinesList.push({
                id: `${betShareData.betType}_${betShareData.lineId}_${betShareData.combId}_${betShareData.poolId}_${comb}`,
                matchId: betShareData.tournamentId, // the original code used matchId, but it should be tournamentId for tournament
                comb,
                betType: betShareData.betType,
                poolId: betShareData.poolId,
                combId: betShareData.combId,
                lineId: betShareData.lineId,
                currentComb: currentCombination,
                currentLine,
                currentPool,
                currentCombNameObj,
                oddsVal: betShareData.betSlipPool.lines[0].odds // use the odds from betshare API directly
            });

            checkedLinesList.forEach((item) => {
                if (checkedLinesObj[item.id]) {
                    checkedLinesObj[item.id].combs.push(item);
                } else {
                    checkedLinesObj[item.id] = {
                        combs: [item],
                        matchId: item.matchId,
                        betType: item.betType,
                        specialLine: item.betType === 'TSP' ? item.currentPool : null
                    };
                }
            });
        } else {
            const currentPool = matchData.foPools?.find((pool) => pool.id === betShareData.poolId);
            const currentLine = currentPool?.lines?.find((line) => line.lineId === betShareData.lineId);
            const currentCombination = currentLine?.combinations?.find((comb) => comb.combId === betShareData.combId);

            // Early return if pool/line/combination is not found
            if (![currentPool, currentLine, currentCombination].every(Boolean)) {
                console.error('Missing pool/line/combination data');
                return;
            }

            const currentCombNameObj = currentCombination.selections[0];
            let comb = currentCombNameObj.str;
            if(betShareData.betType === 'CRS'||betShareData.betType === 'FCRS'||betShareData.betType === 'ETCRS'){
                const match = comb.match(/^(\d+):(\d+)$/);
                if(match){
                    let [,h, m] = match;
                    h=h.padStart(2, '0');
                    m=m.padStart(2, '0');
                    comb = `${h}:${m}`
                }
            }

            const currentCondition = currentLine.condition;
            const condition = updateCondition(currentCondition, betShareData.betType, comb);

            checkedLinesList.push({
                // kickOffTime and lineNum are not in used under current circumstances
                id: `${betShareData.betType}_${betShareData.lineId}_${betShareData.combId}_${betShareData.poolId}_${comb}`,
                matchId: betShareData.matchId,
                oddsType: betShareData.betType,
                betType: betShareData.betType,
                instNo: currentPool?.instNo,
                comb,
                poolId: betShareData.poolId,
                combId: betShareData.combId,
                lineId: betShareData.lineId,
                condition,
                currentCombNameObj,
                currentPool,
                oddsVal: betShareData.betSlipPool.lines[0].odds // use the odds from betshare API directly
            });

            checkedLinesList.forEach((item) => {
                checkedLinesObj[item.id] = {
                    combs: [item],
                    matchId: item.matchId,
                    betType: item.betType,
                    instNo: item.instNo,
                    specialLine: item.betType === 'MSP' ? item.currentPool : null,
                    oddsVal: betShareData.betSlipPool.lines[0].odds
                };
            });
        }

        Object.keys(checkedLinesObj).forEach((key) => {
            let betLineObj = { ...checkedLinesObj[key] };
            let id = '';
            
            betLineObj.combs.sort().forEach((item) => {             
                if (isTournamentPool(betShareData.betType) && item.currentPool.match) {
                     let matchInfo = matchList?.find(item => item.tournament?.id === checkedLinesObj[key].matchId)
                    betLineObj.matchInfo = {
                        ...item.currentPool.match,
                        isInteractiveServiceAvailable: true,
                        inplayDelay: (!isTournamentPage(content?.betType)&&matchInfo)?matchInfo.inplayDelay:false
                    };
                    betLineObj.inplayDelay = (!isTournamentPage(content?.betType)&&matchInfo)?matchInfo.inplayDelay:false;
                }
                id += item.id;
            });

            betLineObj.prod = 'FB';
            betLineObj.betType = checkedLinesObj[key].betType;
            betLineObj.instNo = checkedLinesObj[key].instNo;
            betLineObj.id = id;

            if (isTournamentPool(betShareData.betType)) {
                betLineObj = {
                    ...betLineObj,
                    frontEndId: tournamentData?.frontEndId,
                    tournamentNameCH: tournamentData?.name_ch,
                    tournamentNameEN: tournamentData?.name_en,
                    tournamentCode: tournamentData?.code,
                    tournInfo: tournamentData
                };
            } else {
                const homeTeam = matchData?.homeTeam;
                const awayTeam = matchData?.awayTeam;
                const matchTeamEN = `${homeTeam?.name_en} vs ${awayTeam?.name_en}`;
                const matchTeamCH = `${homeTeam?.name_ch} 對 ${awayTeam?.name_ch}`;
                const frontEndId = matchData?.frontEndId;

                betLineObj = {
                    ...betLineObj,
                    frontEndId,
                    inplayDelay: matchData?.inplayDelay,
                    tournamentNameCH: matchData?.tournament?.name_ch,
                    tournamentNameEN: matchData?.tournament?.name_en,
                    tournamentCode: matchData?.tournament?.code,
                    matchTeamEN,
                    matchTeamCH,
                    matchInfo: matchData
                };

                if (['CHP', 'TQL'].includes(checkedLinesObj[key].betType)) {
                    betLineObj.matchTeamEN = null;
                    betLineObj.matchTeamCH = null;
                    betLineObj.frontEndId = matchData?.tournament?.frontEndId;
                    betLineObj.tournInfo = matchData?.tournament;
                }
            }

            betLineObj.noOfBets = betLineObj.combs.length;
            betLineObj.unitBet = parseInt(AllPoolsMinBet[checkedLinesObj[key].betType] || 10, 10);
            betLineObj.bettingSource = betShareData.bettingSource;
            betLineList.push(betLineObj);
        });

        betLineList.sort((a, b) => singleMatchSortFunc(a, b, contentRef.config.AllOddsOrder));

        //Fix add timeout to wait for get param when add betslip 
        handleAddBetslip(betLineList)
        
        // Clear betshare ID after creating the betslip
        Cookies.setData('betshareId', '', true);

        setIsComplete(true)
    }, [fetchMatchLoading, fetchTournamentLoading, matchList]);
};
